import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { PageShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { t, colon } from "@/lib/i18n";

export const GlossaryList = () => {
  const { slug } = useBrand();
  const [q, setQ] = useState("");
  const { data = [] } = useQuery({ queryKey: ["glossary", slug], queryFn: async () => (await api.get("/glossary", { params: { edition: slug } })).data, enabled: !!slug });
  const items = data.filter((g) => `${g.term} ${g.definition}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div id="glossary" data-testid="glossary-section" className="scroll-mt-24">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input data-testid="glossary-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search words")} className="h-10 rounded-full pl-9" aria-label={t("Search words")} />
      </div>
      <dl className="mt-6 grid gap-3 md:grid-cols-2">
        {items.map((g) => (
          <div key={g.term} data-testid="glossary-term" className="rounded-lg border border-border bg-card p-5">
            <dt className="font-heading text-lg font-bold">{g.term}</dt>
            <dd className="mt-2 text-base leading-relaxed">{g.definition}</dd>
            {g.example && <dd className="mt-2 text-sm text-muted-foreground">{t("Example")}{colon()} {g.example}</dd>}
            {g.say_it_simply && <dd className="mt-3 inline-block rounded-full bg-brand-primary/10 px-3 py-1 text-sm font-semibold text-brand-primary">{t("Say it simply")}{colon()} {g.say_it_simply}</dd>}
            {g.first_seen_in && <dd className="mt-2 text-xs"><Link to={g.first_seen_in.startsWith("LEADER-") ? `/executive/sessions/${g.first_seen_in}` : `/lessons/${g.first_seen_in}`} data-testid={`glossary-first-seen-${g.first_seen_in}`} className="font-semibold text-brand-primary hover:underline">{t("First seen in")}{colon()} {g.first_seen_in}</Link></dd>}
          </div>
        ))}
      </dl>
      {!items.length && <p className="mt-6 text-sm text-muted-foreground">{t("No matching words.")}</p>}
    </div>
  );
};

export default function Glossary() {
  return (
    <PageShell>
      <h1 data-testid="glossary-title" className="font-heading text-4xl font-black tracking-tighter">{t("Glossary")}</h1>
      <p className="mt-3 text-muted-foreground">{t("glossary-sub") === "glossary-sub" ? "Plain words for the terms in the lessons." : t("glossary-sub")}</p>
      <div className="mt-8"><GlossaryList /></div>
    </PageShell>
  );
}
